import { ArrowDown, ArrowUp, Pencil, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { UsagePopover } from '@/components/common/UsageInfo'
import type { DropdownAdminOption } from '@/types/adminDropdowns'
import type { DropdownOptionUsage } from '@/types/adminDropdowns'

interface DropdownOptionsGridProps {
  options: DropdownAdminOption[]
  isLoading: boolean
  usageMap: Record<string, { data?: DropdownOptionUsage; isLoading: boolean }>
  onEdit: (option: DropdownAdminOption) => void
  onDelete: (option: DropdownAdminOption) => void
  onMoveUp: (option: DropdownAdminOption) => void
  onMoveDown: (option: DropdownAdminOption) => void
  onFetchUsage: (option: DropdownAdminOption) => void
}

const DropdownOptionsGrid = ({
  options,
  isLoading,
  usageMap,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  onFetchUsage,
}: DropdownOptionsGridProps) => {
  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        Loading dropdown options...
      </div>
    )
  }

  if (options.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
        No dropdown options found for the current filters.
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Value</TableHead>
            <TableHead>Display Label</TableHead>
            <TableHead>Order</TableHead>
            <TableHead>Active</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {options.map((option, index) => {
            const isFirst = index === 0
            const isLast = index === options.length - 1
            const usage = usageMap[option.value]

            return (
              <TableRow key={option.id}>
                <TableCell className="font-medium">{option.value}</TableCell>
                <TableCell>{option.display_label}</TableCell>
                <TableCell>{option.display_order}</TableCell>
                <TableCell>
                  <Badge variant={option.is_active ? 'success' : 'neutral'}>
                    {option.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <UsagePopover
                      usage={usage?.data}
                      isLoading={usage?.isLoading ?? false}
                      label={option.value}
                      onOpen={() => onFetchUsage(option)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => onMoveUp(option)}
                      disabled={isFirst}
                      aria-label={`Move ${option.value} up`}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => onMoveDown(option)}
                      disabled={isLast}
                      aria-label={`Move ${option.value} down`}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => onEdit(option)}
                      aria-label={`Edit ${option.value}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => onDelete(option)}
                      disabled={!option.is_active}
                      aria-label={`Deactivate ${option.value}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

export default DropdownOptionsGrid
